import { useCallback, useEffect, useState } from 'react';

/**
 * One thing to look at: a photograph or a clip, with what it is called and what somebody wrote
 * about it.
 *
 * <p>
 * Deliberately not `ItemMedia` or `BookingPhoto`. The viewer serves a design's gallery, a garment's
 * gallery and a booking's condition evidence, and those are three different DTOs that happen to
 * share five fields — typing against any one of them would make the other two convert on the way in
 * for no benefit.
 * </p>
 */
export interface ViewableMedia {
  id: number;
  url: string;
  /** `'Video'` plays; anything else is shown as a picture. */
  mediaType: string;
  fileName: string;
  caption?: string | null | undefined;
  /** Optional line under the caption — "Taken at issue", "Primary". */
  note?: string | null | undefined;
}

/**
 * Full-screen photo and video viewer.
 *
 * <p>
 * <strong>One viewer for every gallery in the product.</strong> A design's photographs, a garment's
 * own, and the condition evidence on a booking are all looked at the same way, and three lightboxes
 * would be three sets of keyboard handling and three chances for one of them to trap focus wrongly.
 * </p>
 *
 * <p>
 * <strong>Video is a real case, not a nicety.</strong> The upload path has allowed clips since the
 * catalogue module landed, and a viewer that only did images would have quietly made every uploaded
 * video unopenable — visible in a gallery as a tile and dead on click.
 * </p>
 *
 * <p>
 * <strong>The keyboard is the whole interface here.</strong> Arrows move, Escape closes, and the
 * controls are still buttons so a pointer works — but somebody checking eleven garments at a counter
 * is holding a garment in one hand, and reaching for a mouse between each is the thing that stops
 * people looking properly.
 * </p>
 */
export function MediaViewer({
  items,
  startAt = 0,
  title,
  onClose,
}: {
  items: readonly ViewableMedia[];
  /** Which one to open on. Clamped, so a stale index from a deleted row cannot blank the viewer. */
  startAt?: number;
  /** What this gallery is of — "TUX_NAVY-40-001", "Going out". Shown in the header. */
  title?: string | undefined;
  onClose: () => void;
}) {
  const [index, setIndex] = useState(() => clamp(startAt, items.length));

  // Re-clamped when the list changes under the viewer: deleting the last item while looking at it
  // would otherwise leave the index past the end and render nothing at all.
  useEffect(() => {
    setIndex((was) => clamp(was, items.length));
  }, [items.length]);

  const go = useCallback(
    (by: number) => {
      setIndex((was) => {
        if (items.length === 0) {
          return 0;
        }

        // Wraps, because a gallery is a loop at a counter — the eighth photograph and the first are
        // one keystroke apart, which is how somebody actually compares two of them.
        return (was + by + items.length) % items.length;
      });
    },
    [items.length],
  );

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      } else if (event.key === 'ArrowRight') {
        go(1);
      } else if (event.key === 'ArrowLeft') {
        go(-1);
      }
    };

    document.addEventListener('keydown', onKey);

    // The page behind must not scroll while this is open, or dismissing the viewer lands the reader
    // somewhere else in a list of forty garments.
    const previous = document.body.style.overflow;

    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = previous;
    };
  }, [go, onClose]);

  const current = items[index];

  if (!current) {
    return null;
  }

  return (
    <div
      className="fixed inset-0 z-9999 flex flex-col bg-slate-900/90 print:hidden"
      role="dialog"
      aria-modal="true"
      aria-label={title ? `${title} — photographs` : 'Photographs'}
    >
      <header className="flex items-center gap-3 px-4 py-3 text-white">
        <div className="min-w-0 flex-1">
          {title && <p className="truncate text-[13px] font-semibold">{title}</p>}
          <p className="truncate text-[12px] text-white/70">
            {current.fileName}
            {items.length > 1 && ` · ${index + 1} of ${items.length}`}
          </p>
        </div>

        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="flex size-9 cursor-pointer items-center justify-center rounded text-white hover:bg-white/15"
        >
          <i className="icon icon-x text-[18px]" />
        </button>
      </header>

      <div className="relative flex min-h-0 flex-1 items-center justify-center px-4">
        {items.length > 1 && (
          <button
            type="button"
            onClick={() => go(-1)}
            aria-label="Previous"
            className="absolute start-4 flex size-10 cursor-pointer items-center justify-center rounded-full bg-white/15 text-white hover:bg-white/25"
          >
            <i className="icon icon-chevron-left text-[18px]" />
          </button>
        )}

        {current.mediaType === 'Video' ? (
          // `key` on the source: without it React reuses the element across navigation and the
          // player keeps showing the previous clip's last frame until it is scrubbed.
          <video
            key={current.id}
            src={current.url}
            controls
            autoPlay
            className="max-h-full max-w-full rounded"
          />
        ) : (
          <img
            key={current.id}
            src={current.url}
            alt={current.caption || current.fileName}
            className="max-h-full max-w-full rounded object-contain"
          />
        )}

        {items.length > 1 && (
          <button
            type="button"
            onClick={() => go(1)}
            aria-label="Next"
            className="absolute end-4 flex size-10 cursor-pointer items-center justify-center rounded-full bg-white/15 text-white hover:bg-white/25"
          >
            <i className="icon icon-chevron-right text-[18px]" />
          </button>
        )}
      </div>

      {(current.caption || current.note) && (
        <div className="px-4 py-3 text-center">
          {current.caption && <p className="text-[13px] text-white">{current.caption}</p>}
          {current.note && <p className="text-[11.5px] text-white/60">{current.note}</p>}
        </div>
      )}

      {items.length > 1 && (
        <div className="flex justify-center gap-1.5 overflow-x-auto px-4 pb-4">
          {items.map((item, at) => (
            <button
              key={item.id}
              type="button"
              onClick={() => setIndex(at)}
              aria-label={`Show ${item.fileName}`}
              aria-current={at === index}
              className={`size-12 shrink-0 overflow-hidden rounded border-2 ${
                at === index ? 'border-white' : 'border-transparent opacity-60 hover:opacity-100'
              }`}
            >
              {item.mediaType === 'Video' ? (
                <span className="flex size-full items-center justify-center bg-white/15 text-white">
                  <i className="icon icon-play text-[14px]" />
                </span>
              ) : (
                <img src={item.url} alt="" className="size-full object-cover" />
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/** Keeps an index inside a list, and at 0 for an empty one. */
function clamp(index: number, length: number): number {
  if (length <= 0) {
    return 0;
  }

  return Math.min(Math.max(0, index), length - 1);
}
